import { DataTable } from '@app/components/data-table/data-table';
import { DataTableToolbar } from '@app/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import { toPickerPeriods } from '@app/components/multi-period-picker';
import {
  FilterOptions,
  useServerFilterOptions,
} from '@app/components/data-table/hooks/use-server-filter-options';
import useSimpleDialog from '@app/hooks/use-simple-dialog';
import { DataTableRowAction } from '@app/types/data-table';
import {
  PackagingOperationActivityRecord,
  PaginatedPackagingOperationActivityRecordList,
} from '@shared/api/types';
import { parseAsInteger, useQueryState } from 'nuqs';
import React, { useMemo } from 'react';
import SoldPackagingItemSalesEditForm from './sold-packaging-item-sales-edit-form';
import { useSoldPackagingItemSalesTableColumns } from './sold-packaging-item-sales-table-columns';

interface SoldPackagingItemSalesTableProps {
  data: PaginatedPackagingOperationActivityRecordList;
  status: 'error' | 'success' | 'loading';
  isLoading: boolean;
  isFetching: boolean;
  isPreviousData: boolean;
  isFetchedAfterMount: boolean;
}

export default function SoldPackagingItemSalesTable({
  data,
  status,
  isLoading,
  isPreviousData,
  isFetchedAfterMount,
}: SoldPackagingItemSalesTableProps) {
  const [rowAction, setRowAction] =
    React.useState<DataTableRowAction<PackagingOperationActivityRecord> | null>(
      null
    );
  const { showSimpleDialog, SimpleDialogComponent } = useSimpleDialog();
  const [perPage] = useQueryState('perPage', parseAsInteger.withDefault(10));

  const { count } = data || {};
  const pageCount = count ? Math.ceil(count / (perPage || 10)) : 0;

  const { timePeriodsFilter, sitesFilter, companyReportsFilter } =
    useServerFilterOptions(
      ['timePeriods', 'sites', 'companyReports'] as const,
      data?.filters || {}
    );

  const useFilterOptions: FilterOptions<
    ['timePeriods', 'sites', 'companyReports']
  > = useMemo(
    () => ({
      timePeriods: timePeriodsFilter,
      sites: sitesFilter,
      companyReports: companyReportsFilter,
    }),
    [timePeriodsFilter, sitesFilter, companyReportsFilter]
  );

  const { table } = useServerDataTable({
    data: data?.results || [],
    columns: useSoldPackagingItemSalesTableColumns({
      filters: useFilterOptions,
      timePeriods: toPickerPeriods(data?.filters?.timePeriods),
    }),
    shallow: false,
    pageCount,
    initialState: {
      columnVisibility: {
        uuid: false,
        companyReport: false,
      },
      columnOrder: [
        'uuid',
        'period',
        'site',
        'customer',
        'quantity',
        'totalWeight',
        'unit',
      ],
    },
  });
  return (
    <>
      <DataTable
        table={table}
        isLoading={(!isFetchedAfterMount && isPreviousData) || isLoading}
        tableLoading={!data && status === 'loading'}
        className="bg-gray-00 text-left"
        rowClassName={() => 'cursor-pointer hover:bg-gray-50'}
        onRowClick={(row) => {
          row && setRowAction({ variant: 'update', row });
          showSimpleDialog();
        }}
      >
        <DataTableToolbar table={table} />
      </DataTable>

      {SimpleDialogComponent({
        header: 'Edit sale',
        contentClassName: 'w-full max-w-3xl',
        open: rowAction?.variant === 'update',
        setOpen: () => setRowAction(null),
        content: (
          <SoldPackagingItemSalesEditForm
            onOpenChange={() => setRowAction(null)}
            packagingActivityRecord={
              rowAction?.row.original as PackagingOperationActivityRecord
            }
            numberOfSales={data?.count || 0}
          />
        ),
      })}
    </>
  );
}
